import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useQuery } from "@tanstack/react-query";
import { isBefore } from "date-fns";
import {
  CheckIcon,
  CopyIcon,
  EyeIcon,
  LoaderCircleIcon,
  ReplyIcon,
  SendIcon,
  Share2Icon,
  PencilIcon,
  TargetIcon,
} from "lucide-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { formatDate, formatSecondsToDuration } from "simplydo/core/dates";
import { getAnonymityCheckExcludedItems } from "simplydo/core/ideas";
import { initUseQuery } from "simplydo/core/query";
import {
  getChecklistIssueText,
  getChecklistOptionalTotal,
} from "simplydo/core/submissibility";
import { OpenAPI } from "simplydo/interfaces";
import { MarkdownViewer } from "simplydo/react/richtext";
import { useTheme } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { FacebookIcon } from "simplydo/react/ui/brand-icon";
import { Button, ButtonSize, ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import {
  Dropdown,
  DropdownItem,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H2, H4 } from "simplydo/react/ui/header";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { List, ListItem } from "simplydo/react/ui/list";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Modal, ModalActions, ModalContent } from "simplydo/react/ui/modal";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import IdeaTags from "components/ideas/Tags";
import { useIdea } from "components/ideas/hooks/useIdea";
import {
  getIdeaSubmissionChecklist,
  scrollToSubmissionChecklist,
} from "components/ideas/submissionChecklist";

import ModalCarousel, { ModalCarouselScreen } from "../ModalCarousel";

const CHALLENGE_DEADLINE_AI_CUTOFF_MS = 30 * 60 * 1000;

/** Evaluated at call time — do not memoize over Date.now(). */
const isWithinChallengeDeadlineAiCutoff = (
  deadline: string | Date | null | undefined,
): boolean => {
  if (!deadline) {
    return false;
  }
  const msUntilDeadline = new Date(deadline).getTime() - Date.now();
  return (
    msUntilDeadline > 0 && msUntilDeadline <= CHALLENGE_DEADLINE_AI_CUTOFF_MS
  );
};

/** Steps included for the current wizard session (fixed when the carousel opens). */
type SubmissionWizardSteps = {
  fieldReminder: boolean;
  name: boolean;
  tags: boolean;
  aiFeedback: boolean;
  anonymityCheck: boolean;
};

type IdeaSubmissionNextStepProps = {
  to?: string;
  icon: IconData;
  text: string;
  onClick?: () => void;
};

const IdeaSubmissionNextStep = ({
  to,
  icon,
  text,
  onClick,
}: IdeaSubmissionNextStepProps) => {
  return (
    <div style={{ fontSize: "larger", cursor: "pointer" }}>
      {to ? (
        <ButtonLink onClick={onClick} color="secondary" to={to}>
          <Icon style={{ paddingRight: 3 }} icon={icon} />
          {text}
        </ButtonLink>
      ) : (
        <Button onClick={onClick} color="secondary">
          <Icon style={{ paddingRight: 3 }} icon={icon} />
          {text}
        </Button>
      )}
    </div>
  );
};

type SubmitIdeaButtonProps = {
  updateIdea: (data: any, fields: string[], save?: boolean) => void;
  onSubmit: (toSubmit: boolean) => void;
  isSaving?: boolean;
  style?: React.CSSProperties;
  size?: ButtonSize;
  unsaved?: boolean;
  isStatusBar?: boolean;
};

const SubmitIdeaButton = ({
  updateIdea,
  onSubmit,
  isSaving,
  style,
  size = "small",
  unsaved,
  isStatusBar,
}: SubmitIdeaButtonProps) => {
  const [idea] = useIdea();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionWizardOpen, setSubmissionWizardOpen] = useState(false);
  const [wizardSteps, setWizardSteps] = useState<SubmissionWizardSteps | null>(
    null,
  );
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [showAiFeedback, setShowAiFeedback] = useState(true);
  const [showAnonymityCheck, setShowAnonymityCheck] = useState(true);
  const [loadingAIIdeaName, setLoadingAIIdeaName] = useState(false);
  const theme = useTheme();

  const userId = user?._id;

  const isSuperadmin = useMemo(
    () => util.hasPermission(user, "super.viewDashboard"),
    [user],
  );

  const canEditIdea = util.canEditIdea(user, idea);
  const canManageChallenge = util.canManageChallenge(
    user,
    idea?.ownerChallenge,
  );
  const canUpdateIdeaStatus = util.hasPermission(
    user,
    "challenge.updateIdeaStatus",
    idea?.challenge,
  );
  const canEditIdeas = util.hasPermission(
    user,
    "challenge.editIdeas",
    idea?.challenge,
  );
  const canManageChallenges = util.hasPermission(
    user,
    "org.manageChallenges",
    idea?.ownerChallenge?.organisation,
  );

  const aiIdeaFeedbackQueryEnabled =
    !!userId &&
    isSuperadmin &&
    canEditIdea &&
    !!idea?.ownerChallenge?.aiIdeaFeedback &&
    !idea?.isSubmitted;

  const aiFeedbackTaskQuery = useQuery(
    initUseQuery(
      api.users.getLatestLongRunningTask,
      [userId!, "idea", idea?._id, "aiIdeaFeedback"],
      {
        isEnabled: aiIdeaFeedbackQueryEnabled,
        refetchInterval: (data) => {
          if (data.state?.data?.task?.status === "pending") {
            return 3000;
          }
          return false;
        },
        onSuccess: (data) => {
          if (data?.task?.status === "success") {
            api.invalidateQueries(api.ideas.getAiIdeaFeedback, [idea?._id]);
            setShowAiFeedback(true);
          }
        },
      },
    ),
  );
  const aiFeedbackTask = aiFeedbackTaskQuery.data?.task;

  const aiIdeaFeedbackQuery = useQuery(
    initUseQuery(api.ideas.getAiIdeaFeedback, [idea?._id], {
      isEnabled: aiIdeaFeedbackQueryEnabled,
    }),
  );
  const aiFeedback = aiIdeaFeedbackQuery.data?.aiIdeaFeedback ?? null;

  const aiIdeaAnonymityCheckQueryEnabled =
    !!userId &&
    isSuperadmin &&
    canEditIdea &&
    !!idea?.ownerChallenge?.aiIdeaAnonymityCheck &&
    !idea?.isSubmitted;

  const anonymityCheckTaskQuery = useQuery(
    initUseQuery(
      api.users.getLatestLongRunningTask,
      [userId!, "idea", idea?._id, "aiIdeaAnonymityCheck"],
      {
        isEnabled: aiIdeaAnonymityCheckQueryEnabled,
        refetchInterval: (data) => {
          if (data.state?.data?.task?.status === "pending") {
            return 3000;
          }
          return false;
        },
        onSuccess: (data) => {
          if (data?.task?.status === "success") {
            api.invalidateQueries(api.ideas.getAiIdeaAnonymityCheck, [
              idea._id,
            ]);
            setShowAnonymityCheck(true);
          }
        },
      },
    ),
  );
  const anonymityCheckTask = anonymityCheckTaskQuery.data?.task;

  const aiIdeaAnonymityCheckQuery = useQuery(
    initUseQuery(api.ideas.getAiIdeaAnonymityCheck, [idea?._id], {
      isEnabled: aiIdeaAnonymityCheckQueryEnabled,
    }),
  );
  const anonymityCheck =
    aiIdeaAnonymityCheckQuery.data?.aiIdeaAnonymityCheck ?? null;
  const lastCheckedForIdentifiableDetails =
    aiIdeaAnonymityCheckQuery.data?.lastCheckedForIdentifiableDetails ?? null;

  const canSubmitWithRequiredFieldsMissing = useMemo(
    () => canManageChallenges || canEditIdeas,
    [canManageChallenges, canEditIdeas],
  );

  const defaultName = `${t("common:capitalise", { key: "generic.idea" })} for ${idea?.ownerChallenge?.name}`;
  const isDefaultName = idea && idea?.name === defaultName;

  const isSubmissible = idea?.submissibilityStatus?.isSubmissible;
  const tagBeforeSubmission = idea?.ownerChallenge?.tagBeforeSubmission;
  const hasTags = (idea?.tags || []).length > 0;
  const hasTagList = (idea?.ownerChallenge?.tagList ?? []).length > 0;

  const challengeIsClosed = idea?.ownerChallenge?.stage === "closed";
  const submissionDeadline = idea?.submissionDeadline?.deadline;

  const isClosedToSubmissions =
    challengeIsClosed &&
    (!submissionDeadline || isBefore(new Date(submissionDeadline), new Date()));
  const isBlockedByIncompleteFields =
    !isSubmissible && !canSubmitWithRequiredFieldsMissing;
  // The status bar button stays clickable so it can point users to the submission checklist
  const showsChecklistOnClick = !!isStatusBar && isBlockedByIncompleteFields;

  let tooltip: string | null = null;
  if (isClosedToSubmissions) {
    tooltip = t("ideas.submit.tooltip.closed");
  } else if (showsChecklistOnClick) {
    tooltip = t("ideas.submit.tooltip.showChecklist");
  } else if (isBlockedByIncompleteFields) {
    tooltip = t("ideas.submit.tooltip.incomplete");
  }

  // Cached per idea, so this is shared with the idea body and stable between renders
  const submissionChecklist = getIdeaSubmissionChecklist(user, idea);
  const optionalRemainingTotal = getChecklistOptionalTotal(submissionChecklist);
  // Only managers can submit with required fields missing, regular users are blocked before the wizard
  const isSubmittingWithRequiredFieldsMissing =
    !isSubmissible && canSubmitWithRequiredFieldsMissing;

  const loadAIIdeaName = useCallback(() => {
    setLoadingAIIdeaName(true);
    api.ideas.createIdeaNameWithAI(
      idea?._id,
      ({ success, name: aiName }) => {
        if (success) {
          updateIdea(aiName, ["name"]);
        }
        setLoadingAIIdeaName(false);
      },
      () => {
        setLoadingAIIdeaName(false);
      },
    );
  }, [idea?._id, updateIdea]);

  const ideaName = idea?.name;
  const isSubmitted = idea?.isSubmitted;
  const ownerChallengeName = idea?.ownerChallenge?.name;

  const needsName = useMemo(
    () =>
      !isSubmitted &&
      (!ideaName ||
        ideaName ===
          `${t("common:capitalise", { key: "generic.idea" })} for ${ownerChallengeName}`),
    [isSubmitted, ideaName, ownerChallengeName, t],
  );

  const showTagsStep =
    tagBeforeSubmission && !hasTags && hasTagList && !isSubmitted;

  const hasPreviousAiFeedback = !!(
    aiFeedback?.generatedAt || idea?.aiIdeaFeedback?.generatedAt
  );
  const hasPreviousAnonymityCheck = !!(
    anonymityCheck?.generatedAt || idea?.aiIdeaAnonymityCheck?.generatedAt
  );

  const anonymityCheckBusy =
    anonymityCheckTaskQuery.isLoading ||
    anonymityCheckTask?.status === "pending";

  const anonymityCheckPassed = useMemo(
    () =>
      !!lastCheckedForIdentifiableDetails &&
      !!anonymityCheck &&
      (anonymityCheck.findings?.length ?? 0) === 0,
    [lastCheckedForIdentifiableDetails, anonymityCheck],
  );

  const anonymityCheckExcludedItems = useMemo(
    () => getAnonymityCheckExcludedItems(idea?.ownerIdeaTemplate),
    [idea?.ownerIdeaTemplate],
  );

  const submitLogic = useCallback(
    (toSubmit: boolean, bypassConfirmation = false) => {
      const doSubmit = () => {
        const upd: OpenAPI.PUT<"/ideas/{id}/status">["requestBody"] = {
          isSubmitted: toSubmit,
        };
        setIsSubmitting(true);
        api.ideas.updateStatus(
          idea?._id,
          upd,
          (newIdea) => {
            util.onUpdateIdea(newIdea, newIdea);

            if (newIdea.isSubmitted) {
              setSubmitModalOpen(true);
              onSubmit(toSubmit);
              setSubmissionWizardOpen(false);
              setWizardSteps(null);
              setIsSubmitting(false);
            } else {
              toast.success(t("ideas.submit.confirm.unsubmitSuccess"));
              onSubmit(toSubmit);
              setSubmissionWizardOpen(false);
              setWizardSteps(null);
              setIsSubmitting(false);
            }

            api.invalidateQueries(api.challenges.getIdeas, [newIdea.challenge]);
          },
          (err) => {
            if (err.status === 400) {
              updateIdea("closed", ["ownerChallenge", "stage"], true);
            }
            toast.error(err.message);
            setIsSubmitting(false);
          },
        );
      };

      if (toSubmit && bypassConfirmation) {
        doSubmit();
        return;
      }

      if (!toSubmit) {
        util
          .confirm(
            t("ideas.submit.confirm.withdraw.title"),
            t("ideas.submit.confirm.withdraw.info"),
          )
          .then(() => {
            doSubmit();
          })
          .catch(() => {});
      }
    },
    [t, onSubmit, updateIdea, idea?._id],
  );

  const getAIIdeaFeedbackCooldownSeconds = useCallback(() => {
    if (!aiFeedback?.generatedAt) {
      return null;
    }

    const generatedAt = new Date(aiFeedback.generatedAt);
    const requestCount = aiFeedback.requestCount || 0;

    const waitWindows = [5, 30, 60, 1440];
    const waitIndex = Math.min(
      Math.max(requestCount - 1, 0),
      waitWindows.length - 1,
    );
    const waitMinutes =
      waitWindows[waitIndex] ?? waitWindows[waitWindows.length - 1]!;
    const nextAllowedAt = generatedAt.getTime() + waitMinutes * 60 * 1000;
    const remainingMs = nextAllowedAt - Date.now();
    if (remainingMs <= 0) {
      return null;
    }
    return Math.ceil(remainingMs / 1000);
  }, [aiFeedback]);

  const [aiIdeaFeedbackCooldownSeconds, setAiIdeaFeedbackCooldownSeconds] =
    useState<number | null>(getAIIdeaFeedbackCooldownSeconds());

  useEffect(() => {
    if (!submissionWizardOpen || !wizardSteps?.aiFeedback) {
      return;
    }

    const updateCooldown = () => {
      setAiIdeaFeedbackCooldownSeconds(getAIIdeaFeedbackCooldownSeconds());
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [
    submissionWizardOpen,
    wizardSteps?.aiFeedback,
    getAIIdeaFeedbackCooldownSeconds,
    aiFeedback,
  ]);

  const getAIIdeaAnonymityCheckCooldownSeconds = useCallback(() => {
    if (!anonymityCheck?.generatedAt) {
      return null;
    }

    const generatedAt = new Date(anonymityCheck.generatedAt);
    const requestCount = anonymityCheck.requestCount || 0;

    const waitWindows = [5, 30, 60, 1440];
    const waitIndex = Math.min(
      Math.max(requestCount - 1, 0),
      waitWindows.length - 1,
    );
    const waitMinutes =
      waitWindows[waitIndex] ?? waitWindows[waitWindows.length - 1]!;
    const nextAllowedAt = generatedAt.getTime() + waitMinutes * 60 * 1000;
    const remainingMs = nextAllowedAt - Date.now();
    if (remainingMs <= 0) {
      return null;
    }
    return Math.ceil(remainingMs / 1000);
  }, [anonymityCheck]);

  const [
    aiIdeaAnonymityCheckCooldownSeconds,
    setAiIdeaAnonymityCheckCooldownSeconds,
  ] = useState<number | null>(getAIIdeaAnonymityCheckCooldownSeconds());

  useEffect(() => {
    if (!submissionWizardOpen || !wizardSteps?.anonymityCheck) {
      return;
    }

    const updateCooldown = () => {
      setAiIdeaAnonymityCheckCooldownSeconds(
        getAIIdeaAnonymityCheckCooldownSeconds(),
      );
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [
    submissionWizardOpen,
    wizardSteps?.anonymityCheck,
    getAIIdeaAnonymityCheckCooldownSeconds,
    anonymityCheck,
  ]);

  const aiFeedbackBusy =
    aiFeedbackTaskQuery.isLoading || aiFeedbackTask?.status === "pending";

  const requestAIIdeaFeedback = useCallback(() => {
    if (!userId) {
      return;
    }
    api.users.createLongRunningTask(
      userId,
      { forType: "idea", forId: idea?._id, type: "aiIdeaFeedback" },
      () => {
        api.invalidateQueries(api.users.getLatestLongRunningTask, [
          userId,
          "idea",
          idea?._id,
          "aiIdeaFeedback",
        ]);
        api.invalidateQueries(api.ideas.getAiIdeaFeedback, [idea?._id]);
        setAiIdeaFeedbackCooldownSeconds(getAIIdeaFeedbackCooldownSeconds());
      },
      (err) => {
        if (err.status === 429) {
          const match = err.message?.match(/(\d+)/);
          if (match && !isSuperadmin) {
            toast.error(
              `Please wait ${match[1]} seconds before requesting another review.`,
            );
          } else {
            toast.error(err.message);
          }
        } else {
          toast.error(err.message);
        }
      },
    );
  }, [userId, idea?._id, getAIIdeaFeedbackCooldownSeconds, isSuperadmin]);

  const requestAIIdeaAnonymityCheck = useCallback(() => {
    if (!userId) {
      return;
    }
    api.users.createLongRunningTask(
      userId,
      { forType: "idea", forId: idea?._id, type: "aiIdeaAnonymityCheck" },
      () => {
        api.invalidateQueries(api.users.getLatestLongRunningTask, [
          userId,
          "idea",
          idea?._id,
          "aiIdeaAnonymityCheck",
        ]);
        api.invalidateQueries(api.ideas.getAiIdeaAnonymityCheck, [idea?._id]);
        setAiIdeaAnonymityCheckCooldownSeconds(
          getAIIdeaAnonymityCheckCooldownSeconds(),
        );
      },
      (err) => {
        if (err.status === 429) {
          const match = err.message?.match(/(\d+)/);
          if (match && !isSuperadmin) {
            toast.error(
              `Please wait ${match[1]} seconds before requesting another review.`,
            );
          } else {
            toast.error(err.message);
          }
        } else {
          toast.error(err.message);
        }
      },
    );
  }, [userId, idea?._id, getAIIdeaAnonymityCheckCooldownSeconds, isSuperadmin]);

  const closeSubmissionWizard = useCallback(() => {
    setSubmissionWizardOpen(false);
    setWizardSteps(null);
  }, []);

  const openSubmissionWizard = useCallback(() => {
    // Recompute at open time so the 30-minute cutoff is not stale from an
    // earlier render (wizard steps are snapshotted for the session).
    const isWithin30MinsOfChallengeDeadline = isWithinChallengeDeadlineAiCutoff(
      idea?.ownerChallenge?.deadline,
    );
    const showAiFeedbackStep =
      isSuperadmin &&
      !!idea?.ownerChallenge?.aiIdeaFeedback &&
      !idea?.isSubmitted &&
      (!isWithin30MinsOfChallengeDeadline || hasPreviousAiFeedback);
    const showAnonymityCheckStep =
      isSuperadmin &&
      !!idea?.ownerChallenge?.aiIdeaAnonymityCheck &&
      !idea?.isSubmitted &&
      (!isWithin30MinsOfChallengeDeadline || hasPreviousAnonymityCheck);

    setWizardSteps({
      fieldReminder:
        isSubmittingWithRequiredFieldsMissing || optionalRemainingTotal > 0,
      name: needsName,
      tags: !!showTagsStep,
      aiFeedback: showAiFeedbackStep,
      anonymityCheck: showAnonymityCheckStep,
    });
    if (needsName) {
      loadAIIdeaName();
    }
    setShowAiFeedback(false);
    setShowAnonymityCheck(false);
    setSubmissionWizardOpen(true);
  }, [
    isSubmittingWithRequiredFieldsMissing,
    optionalRemainingTotal,
    needsName,
    showTagsStep,
    idea?.ownerChallenge?.deadline,
    idea?.ownerChallenge?.aiIdeaFeedback,
    idea?.ownerChallenge?.aiIdeaAnonymityCheck,
    idea?.isSubmitted,
    isSuperadmin,
    hasPreviousAiFeedback,
    hasPreviousAnonymityCheck,
    loadAIIdeaName,
  ]);

  const submitIdea = useCallback(
    (toSubmit: boolean) => {
      if (toSubmit) {
        openSubmissionWizard();
      } else {
        submitLogic(false);
      }
    },
    [openSubmissionWizard, submitLogic],
  );

  const submissionScreens = useMemo((): ModalCarouselScreen[] => {
    if (!wizardSteps) {
      return [];
    }

    const screens: ModalCarouselScreen[] = [];

    if (wizardSteps.fieldReminder) {
      const blockingSections = submissionChecklist.filter(
        (section) => section.blockingFields.length > 0,
      );
      const optionalSections = submissionChecklist.filter(
        (section) => section.optionalRemaining > 0,
      );
      screens.push({
        title: isSubmittingWithRequiredFieldsMissing
          ? t("ideas.submit.confirm.submit.skipRequiredTitle")
          : t("ideas.submit.optionalReminder.title"),
        content: (
          <>
            {isSubmittingWithRequiredFieldsMissing ? (
              <>
                <Message color="orange" size="small">
                  <MessageContent>
                    {t("ideas.submit.confirm.submit.skipRequiredInfo")}
                  </MessageContent>
                </Message>
                <H4>{t("ideas.submit.optionalReminder.requiredHeading")}</H4>
                <List variant="bulleted">
                  {blockingSections.flatMap((section) =>
                    section.blockingFields.map((field, index) => {
                      const issueText = getChecklistIssueText(field, t);
                      return (
                        <ListItem key={field.id ?? `${section.key}-${index}`}>
                          {section.name}: {field.title}
                          {issueText ? ` (${issueText})` : ""}
                        </ListItem>
                      );
                    }),
                  )}
                </List>
              </>
            ) : null}
            {optionalRemainingTotal > 0 ? (
              <>
                {isSubmittingWithRequiredFieldsMissing ? (
                  <H4>{t("ideas.submit.optionalReminder.optionalHeading")}</H4>
                ) : null}
                <p>
                  {t("ideas.submit.optionalReminder.info", {
                    count: optionalRemainingTotal,
                  })}
                </p>
                <List variant="bulleted">
                  {optionalSections.map((section) => (
                    <ListItem key={section.key}>
                      {t("ideas.submit.optionalReminder.section", {
                        name: section.name,
                        count: section.optionalRemaining,
                      })}
                    </ListItem>
                  ))}
                </List>
              </>
            ) : null}
          </>
        ),
        actions: [
          {
            label: t("ideas.submit.optionalReminder.review"),
            onClick: () => {
              closeSubmissionWizard();
              scrollToSubmissionChecklist();
            },
          },
        ],
      });
    }

    if (wizardSteps.name) {
      screens.push({
        title: t("ideas.submit.naming.title"),
        disableNext:
          loadingAIIdeaName ||
          !idea?.name ||
          isDefaultName ||
          !!isSaving ||
          !!unsaved,
        content: loadingAIIdeaName ? (
          <div style={{ padding: "20px 0" }}>
            <Loader>{`Generating ${t("generic.idea")} name...`}</Loader>
          </div>
        ) : (
          <>
            <p>
              {t("ideas.submit.naming.info")}
              {". "}
              {`${t("ideas.submit.naming.help")}`}
            </p>
            <Input
              autoFocus
              placeholder={t("ideas.submit.naming.placeholder")}
              value={isDefaultName ? "" : idea?.name || ""}
              fluid
              onChange={(e) =>
                updateIdea(e.target.value || defaultName, ["name"])
              }
              style={{ minWidth: theme.sizes.isMobile ? "80vw" : 400 }}
            />
          </>
        ),
      });
    }

    if (wizardSteps.tags) {
      screens.push({
        title: `${t("common:capitalise", { key: "generic.idea" })} tags`,
        disableNext: !hasTags,
        content: (
          <>
            <p>{t("ideas.statusBar.tag.info")}</p>
            <IdeaTags idea={idea} updateIdea={updateIdea} isSubmission={true} />
          </>
        ),
      });
    }

    if (wizardSteps.aiFeedback) {
      screens.push({
        title: `Automated ${t("generic.idea")} review`,
        content: (
          <>
            <p>
              Your {t("generic.idea")} will be assessed against the{" "}
              {t("generic.challenge")} brief. Run an automated review to spot
              gaps, missing answers, or areas that need strengthening before you
              submit.
            </p>
            <p>
              This review is automated and may not catch everything. You are
              responsible for checking your submission before you submit.
            </p>
            {aiFeedbackBusy ? (
              <Message>
                Generating automated {t("generic.idea")} feedback...
              </Message>
            ) : null}
            {aiFeedback?.generatedAt && (
              <Message style={{ marginTop: 16 }}>
                <Accordion>
                  <AccordionTitle
                    active={showAiFeedback}
                    index={0}
                    onClick={() => {
                      setShowAiFeedback((open) => !open);
                    }}
                  >
                    {showAiFeedback
                      ? `${t("common:capitalise", { key: "generic.idea" })} review`
                      : `Show automated ${t("generic.idea")} review`}
                  </AccordionTitle>
                  <AccordionContent active={showAiFeedback} index={0}>
                    <MessageContent>
                      <p
                        style={{
                          marginBottom: 8,
                          color: "rgba(0, 0, 0, 0.6)",
                          fontSize: "0.9em",
                        }}
                      >
                        Generated at:{" "}
                        {formatDate(new Date(aiFeedback!.generatedAt!))}
                      </p>
                      {aiFeedback?.briefFitScore &&
                      aiFeedback.briefFitScore >= 0.8 ? (
                        <p
                          style={{
                            marginBottom: 8,
                            color: "rgba(0, 0, 0, 0.75)",
                            fontSize: "0.95em",
                          }}
                        >
                          Our automated review found that your{" "}
                          {t("generic.idea")} generally aligns with the brief,
                          so there is no additional feedback at this time.
                          Please note that this does not replace a manual
                          review, and we recommend reviewing your{" "}
                          {t("generic.idea")} carefully before submission.
                        </p>
                      ) : (
                        <>
                          {aiFeedback?.content && (
                            <MarkdownViewer value={aiFeedback.content} />
                          )}
                          {(aiFeedback?.strengths?.length ?? 0) > 0 && (
                            <>
                              <H4 style={{ marginTop: 16, marginBottom: 8 }}>
                                Strengths
                              </H4>
                              <List variant="bulleted" compact>
                                {aiFeedback?.strengths?.map((item, index) => (
                                  <ListItem key={`strength-${index}`}>
                                    {item}
                                  </ListItem>
                                ))}
                              </List>
                            </>
                          )}
                          {(aiFeedback?.weaknesses?.length ?? 0) > 0 && (
                            <>
                              <H4 style={{ marginTop: 16, marginBottom: 8 }}>
                                Weaknesses
                              </H4>
                              <List variant="bulleted" compact>
                                {aiFeedback?.weaknesses?.map((item, index) => (
                                  <ListItem key={`weakness-${index}`}>
                                    {item}
                                  </ListItem>
                                ))}
                              </List>
                            </>
                          )}
                          {(aiFeedback?.missingOrPartialAnswers?.length ?? 0) >
                            0 && (
                            <>
                              <H4 style={{ marginTop: 16, marginBottom: 8 }}>
                                Missing or partial answers
                              </H4>
                              <List variant="bulleted" compact>
                                {aiFeedback?.missingOrPartialAnswers?.map(
                                  (item, index) => (
                                    <ListItem key={`missing-${index}`}>
                                      {item}
                                    </ListItem>
                                  ),
                                )}
                              </List>
                            </>
                          )}
                          {(aiFeedback?.recommendations?.length ?? 0) > 0 && (
                            <>
                              <H4 style={{ marginTop: 16, marginBottom: 8 }}>
                                Recommendations
                              </H4>
                              <List variant="bulleted" compact>
                                {aiFeedback?.recommendations?.map(
                                  (item, index) => (
                                    <ListItem key={`recommendation-${index}`}>
                                      {item}
                                    </ListItem>
                                  ),
                                )}
                              </List>
                            </>
                          )}
                        </>
                      )}
                    </MessageContent>
                  </AccordionContent>
                </Accordion>
              </Message>
            )}
            <div style={{ marginTop: 16 }}>
              {!aiFeedbackBusy &&
              !isSuperadmin &&
              !!aiIdeaFeedbackCooldownSeconds ? (
                <p
                  style={{
                    marginBottom: 8,
                    color: "rgba(0, 0, 0, 0.6)",
                    fontSize: "0.95em",
                  }}
                >
                  You can request another review in{" "}
                  {formatSecondsToDuration(aiIdeaFeedbackCooldownSeconds, {
                    format: { hours: "h", minutes: "m", seconds: "s" },
                  })}
                  .
                </p>
              ) : null}
              <Button
                loading={aiFeedbackBusy}
                disabled={
                  aiFeedbackBusy ||
                  (!!aiIdeaFeedbackCooldownSeconds && !isSuperadmin)
                }
                onClick={requestAIIdeaFeedback}
                color="primary"
              >{`Review my ${t("generic.idea")}`}</Button>
            </div>
          </>
        ),
      });
    }

    if (wizardSteps.anonymityCheck) {
      screens.push({
        title: t("ideas.submit.anonymityCheck.title"),
        content: (
          <>
            <p>{t("ideas.submit.anonymityCheck.intro")}</p>
            <p>{t("ideas.submit.anonymityCheck.automatedCheckDisclaimer")}</p>
            {anonymityCheckExcludedItems.length > 0 ? (
              <Message size="small" color="blue" style={{ marginTop: 16 }}>
                <MessageHeader>
                  {t("ideas.submit.anonymityCheck.excludedSectionsTitle")}
                </MessageHeader>
                <MessageContent>
                  <p>
                    {t("ideas.submit.anonymityCheck.excludedSectionsIntro")}
                  </p>
                  <List variant="bulleted" compact>
                    {anonymityCheckExcludedItems.map((item) => (
                      <ListItem key={JSON.stringify(item)}>
                        {item.kind === "edi" ||
                        item.kind === "businessProfile" ||
                        item.kind === "managerSection"
                          ? `${item.name}`
                          : `${item.sectionName}: ${item.fieldTitle}`}
                      </ListItem>
                    ))}
                  </List>
                  <p>
                    {t("ideas.submit.anonymityCheck.excludedSectionsFooter")}
                  </p>
                </MessageContent>
              </Message>
            ) : null}
            {anonymityCheckBusy ? (
              <Message>
                Checking your {t("generic.idea")} for identifying information...
              </Message>
            ) : null}
            {lastCheckedForIdentifiableDetails && anonymityCheck ? (
              <Message style={{ marginTop: 16 }}>
                <Accordion>
                  <AccordionTitle
                    active={showAnonymityCheck}
                    index={0}
                    onClick={() => {
                      setShowAnonymityCheck((open) => !open);
                    }}
                  >
                    {showAnonymityCheck
                      ? "Anonymity check"
                      : `Show automated ${t("generic.idea")} anonymity check`}
                  </AccordionTitle>
                  <AccordionContent active={showAnonymityCheck} index={0}>
                    <MessageContent>
                      <p
                        style={{
                          marginBottom: 8,
                          color: "rgba(0, 0, 0, 0.6)",
                          fontSize: "0.9em",
                        }}
                      >
                        Generated at:{" "}
                        {formatDate(lastCheckedForIdentifiableDetails)}
                      </p>
                      {anonymityCheckPassed ? (
                        <p
                          style={{
                            marginBottom: 8,
                            color: "rgba(0, 0, 0, 0.75)",
                            fontSize: "0.95em",
                          }}
                        >
                          {t("ideas.submit.anonymityCheck.passedNoFindings")}
                        </p>
                      ) : (
                        <>
                          {anonymityCheck.content ? (
                            <MarkdownViewer value={anonymityCheck.content} />
                          ) : null}
                          {(anonymityCheck.findings?.length ?? 0) > 0 && (
                            <>
                              <H4 style={{ marginTop: 16, marginBottom: 8 }}>
                                Detected items
                              </H4>
                              <List variant="bulleted" compact>
                                {anonymityCheck.findings?.map((item, index) => (
                                  <ListItem key={`finding-${index}`}>
                                    {item.message}
                                  </ListItem>
                                ))}
                              </List>
                            </>
                          )}
                        </>
                      )}
                    </MessageContent>
                  </AccordionContent>
                </Accordion>
              </Message>
            ) : null}
            <div style={{ marginTop: 16 }}>
              {!anonymityCheckBusy &&
              !isSuperadmin &&
              !!aiIdeaAnonymityCheckCooldownSeconds ? (
                <p
                  style={{
                    marginBottom: 8,
                    color: "rgba(0, 0, 0, 0.6)",
                    fontSize: "0.95em",
                  }}
                >
                  You can request another review in{" "}
                  {formatSecondsToDuration(
                    aiIdeaAnonymityCheckCooldownSeconds,
                    { format: { hours: "h", minutes: "m", seconds: "s" } },
                  )}
                  .
                </p>
              ) : null}
              <Button
                loading={anonymityCheckBusy}
                disabled={
                  anonymityCheckBusy ||
                  (!!aiIdeaAnonymityCheckCooldownSeconds && !isSuperadmin)
                }
                onClick={requestAIIdeaAnonymityCheck}
                color="primary"
              >
                {t("ideas.submit.anonymityCheck.runCheck")}
              </Button>
            </div>
          </>
        ),
      });
    }

    screens.push({
      title: t("ideas.submit.confirm.submit.title"),
      content: (
        <>
          <p>
            {t("ideas.submit.confirm.submit.managersInfo")}{" "}
            {t("ideas.submit.confirm.submit.collaboratorsInfo")}
          </p>
          <p>{t("ideas.submit.confirm.submit.editInfo")}</p>
        </>
      ),
      actions: [
        {
          label: t("generic.submit"),
          icon: isSubmitting ? LoaderCircleIcon : SendIcon,
          primary: true,
          loading: isSubmitting,
          disabled: isSaving || unsaved,
          onClick: () => submitLogic(true, true),
        },
      ],
    });

    return screens;
  }, [
    wizardSteps,
    submissionChecklist,
    optionalRemainingTotal,
    closeSubmissionWizard,
    loadingAIIdeaName,
    idea,
    isDefaultName,
    isSaving,
    unsaved,
    t,
    updateIdea,
    defaultName,
    theme.sizes.isMobile,
    hasTags,
    showAiFeedback,
    aiFeedbackBusy,
    aiFeedback,
    isSuperadmin,
    aiIdeaFeedbackCooldownSeconds,
    requestAIIdeaFeedback,
    showAnonymityCheck,
    anonymityCheckBusy,
    aiIdeaAnonymityCheckCooldownSeconds,
    lastCheckedForIdentifiableDetails,
    anonymityCheck,
    anonymityCheckPassed,
    anonymityCheckExcludedItems,
    requestAIIdeaAnonymityCheck,
    isSubmittingWithRequiredFieldsMissing,
    isSubmitting,
    submitLogic,
  ]);

  const copyLink = useCallback(() => {
    void util.copyToClipboard(
      `${util.getBaseUrl(user?.ownerOrganisation)}/ideas/${idea?._id}`,
    );
  }, [idea, user]);

  return (
    <>
      <ModalCarousel
        open={submissionWizardOpen}
        onClose={closeSubmissionWizard}
        scrolling={!!wizardSteps?.aiFeedback || !!wizardSteps?.anonymityCheck}
        screens={submissionScreens}
        actions={[
          { label: "Continue editing", onClick: closeSubmissionWizard },
        ]}
      />

      <Modal open={submitModalOpen} onClose={() => setSubmitModalOpen(false)}>
        <div
          style={{
            textAlign: "center",
            marginTop: 7,
            marginBottom: 10,
            paddingLeft: 20,
            paddingRight: 20,
          }}
        >
          <div style={{ marginTop: 40, marginBottom: 20 }}>
            <Icon icon={CheckIcon} size="large" color="green" />
          </div>
          <H2 style={{ marginBottom: 20 }}>
            <small>
              Your {t("generic.idea")} was successfully submitted to{" "}
              {idea?.ownerChallenge?.name}
            </small>
          </H2>
          {idea?.ownerChallenge &&
          (idea?.ownerChallenge.ideaVisibility !== "users" ||
            !!idea?.ownerChallenge?.ideaVisibilityLimits?.length) ? null : (
            <>
              <Divider horizontal>
                <H4 style={{ margin: 0 }}>{t("challenges.new.done.info")}</H4>
              </Divider>
              <ModalContent style={{ padding: "0.75rem 1.5rem" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: theme.sizes.isMobile ? "column" : "row",
                    alignItems: "center",
                    justifyContent: "space-around",
                    gap: 12,
                  }}
                >
                  {user?._id ? (
                    <IdeaSubmissionNextStep
                      to={"/challenges/discover"}
                      icon={EyeIcon}
                      text={`Discover ${t("generic.challenges")}`}
                    />
                  ) : null}
                  {idea?.ownerChallenge && (
                    <IdeaSubmissionNextStep
                      to={`/challenges/${idea?.ownerChallenge?._id}`}
                      icon={TargetIcon}
                      text={`View the ${t("generic.challenge")}`}
                    />
                  )}
                  <Dropdown
                    style={{ minWidth: "max-content" }}
                    upward
                    direction="left"
                    trigger={
                      <IdeaSubmissionNextStep
                        icon={Share2Icon}
                        text={`Share your ${t("generic.idea")}`}
                      />
                    }
                  >
                    <DropdownMenu>
                      <DropdownItemLink
                        href={`https://x.com/intent/tweet?text=${util.getBaseUrl(user?.ownerOrganisation, `/ideas/${idea?._id}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div
                          style={{
                            display: "inline",
                            marginRight: 16,
                            marginLeft: 3,
                          }}
                        >
                          𝕏
                        </div>
                        Share on X
                      </DropdownItemLink>
                      <DropdownItemLink
                        href={`https://www.facebook.com/sharer/sharer.php?u=${util.getBaseUrl(user?.ownerOrganisation, `/ideas/${idea?._id}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FacebookIcon />
                        Share on Facebook
                      </DropdownItemLink>
                      <DropdownItem
                        onClick={() => copyLink()}
                        onKeyDown={handleClickForKeyboard(() => copyLink())}
                      >
                        <Icon icon={CopyIcon} />
                        {`Copy link to ${t("generic.idea")}`}
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </ModalContent>
            </>
          )}
        </div>
        <ModalActions>
          <Button onClick={() => setSubmitModalOpen(false)}>
            {t("generic.close")}
          </Button>
          {idea?.ownerChallenge &&
          idea?.ownerChallenge.ideaVisibility === "users" ? (
            <Button
              onClick={() => {
                setSubmitModalOpen(false);
                navigate(`/challenges/${idea.challenge}/ideas`);
              }}
              color="primary"
            >{`View ${t("generic.ideas")} created by others`}</Button>
          ) : (
            <Button
              onClick={() => setSubmitModalOpen(false)}
            >{`Return to my ${t("generic.idea")}`}</Button>
          )}
        </ModalActions>
      </Modal>

      {canEditIdea || canManageChallenge || canUpdateIdeaStatus ? (
        idea?.isSubmitted ? (
          <Button
            style={style}
            size={size}
            onClick={() => submitIdea(false)}
            loading={isSubmitting}
            color="primary"
          >
            <Icon
              icon={
                canEditIdeas || canManageChallenges ? ReplyIcon : PencilIcon
              }
            />
            {canEditIdea
              ? theme.sizes.isMobile || canEditIdeas || canManageChallenges
                ? `Unsubmit ${t("generic.idea")}`
                : t("ideas.editing")
              : t("generic.unsubmit")}
          </Button>
        ) : (
          <Button
            disabled={
              isSaving ||
              unsaved ||
              (!idea?.ownerChallenge && !idea?.isSubmitted) ||
              (isBlockedByIncompleteFields && !showsChecklistOnClick) ||
              ((!idea?.submissionDeadline ||
                (idea?.submissionDeadline &&
                  isBefore(
                    new Date(idea?.submissionDeadline?.deadline),
                    new Date(),
                  ))) &&
                idea?.ownerChallenge?.stage === "closed")
            }
            style={style}
            size={size}
            onClick={
              showsChecklistOnClick
                ? scrollToSubmissionChecklist
                : openSubmissionWizard
            }
            loading={isSubmitting}
            tooltip={tooltip}
            color="primary"
          >
            <Icon icon={isSubmitting ? LoaderCircleIcon : SendIcon} />
            {t("generic.submit")}
          </Button>
        )
      ) : null}
    </>
  );
};

export default SubmitIdeaButton;
